import { useState, useCallback } from 'react';
import Chart, { useChart } from 'src/components/chart';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import Iconify from '../../iconify';
import CustomTooltip from './CustomTooltip';
import { PresenceSegments } from '../types';

/**
 * Compute the percentage of results where only competitors are present.
 * This is the complement of the total visibility rate to 100%.
 *
 * @param totalVisibilityRate The total visibility rate (brand presence).
 * @returns Percentage for the "Competitors only" segment.
 */
const calculateCompetitorsOnly = (totalVisibilityRate: number) => {
    return 100 - totalVisibilityRate;
};

/**
 * PresenceRateChart renders a compact horizontal stacked bar to visualize a presence breakdown
 * across four segments: Organic only, Solo bidder, Top ad position, and Competitors only.
 * The chart is styled as a sparkline (no axes/legend) and reveals a detailed tooltip on hover.
 *
 * Notes:
 * - Expects raw percentage values (0–100) in the input segments.
 * - Values displayed in the tooltip are rounded and clamped for readability.
 * - Colors adapt to the current MUI theme mode.
 *
 * @param data Presence distribution segments used to build the stacked bar.
 * @param data.organicOnly Percentage of results with organic presence only.
 * @param data.soloBidder Percentage of results where the brand is the only bidder.
 * @param data.topAdPosition Percentage of results where the brand holds a top ad position.
 * @param [height=40] Optional chart height in pixels.
 * @returns A React element containing the stacked bar chart with tooltip.
 */
export default function PresenceRateChart({ data, round = 0, height = 40, totalVisibilityRate, colors: customColors }: {
    data: PresenceSegments; height?: number; totalVisibilityRate?: number, colors?: string[], round?: number;
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const defaultColors = ['#18A155', '#C09910', '#F9DE81', 'rgba(145, 158, 171, 0.20)'];

    // Utilise colors passées en props si elles existent, sinon defaultColors
    const colors = customColors && customColors.length === 4 ? customColors : defaultColors;
    const visibilityBase = typeof totalVisibilityRate === 'number' ? totalVisibilityRate : 100;



    type Item = { label: string; value: number; color: string; icon: string };

    const chartOptions = useChart({
        chart: {
            type: 'bar',
            stacked: true,
            parentHeightOffset: 0,
            animations: { enabled: false },
            sparkline: {
                enabled: true,
            },
            toolbar: {
                show: false,
            },
            stackType: 'normal',
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '30%',
                borderRadius: 0,
            },
        },
        stroke: {
            width: 0,
            show: false,
        },
        series: [
            { name: 'Organic only', data: [data.organicOnly] },
            { name: 'Solo bidder', data: [data.soloBidder] },
            { name: 'Top ad position', data: [data.topAdPosition] },
            { name: 'Competitors only', data: [calculateCompetitorsOnly(visibilityBase)] },
        ],
        colors,
        tooltip: {
            enabled: false,
            x: { show: false },
            marker: { show: false },
        },
        legend: {
            show: false,
        },
        grid: {
            show: false,
        },
        xaxis: {
            labels: {
                show: false,
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            max: 100,
            min: 0,
        },
        yaxis: {
            labels: {
                show: false,
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
        },
    });

    // Items avec valeurs brutes (non arrondies)
    const rawItems: Item[] = [
        { label: 'Organic only', value: Math.max(0, data.organicOnly), color: colors[0], icon: 'solar:global-linear' },
        { label: 'Solo bidder', value: Math.max(0, data.soloBidder), color: colors[1], icon: 'carbon:user' },
        { label: 'Top ad position', value: Math.max(0, data.topAdPosition), color: colors[2], icon: 'carbon:user-multiple' },
        { label: 'Competitors only', value: Math.max(0, calculateCompetitorsOnly(visibilityBase)), color: colors[3], icon: 'ri:sword-line' },
    ];

    const items = rawItems.map(it => ({
        ...it,
        value: round === 0 ? Math.round(it.value) : Number(it.value.toFixed(round))
    }));

    const [open, setOpen] = useState(false);
    const handleOpen = useCallback(() => setOpen(true), []);
    const handleClose = useCallback(() => setOpen(false), []);

    return (
        <CustomTooltip
            placement="top"
            disableInteractive
            open={open}
            onClose={handleClose}
            title={
                <Stack direction="column" spacing={0.75}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Presence breakdown
                    </Typography>
                    <Stack direction="column" spacing={0.5}>
                        {items.map((it) => (
                            <Stack key={it.label} direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Iconify icon={it.icon} sx={{ width: 16, height: 16, color: it.color }} />
                                    <Typography variant="caption">{it.label}</Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">{it.value}%</Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            }
        >
            <Box
                onMouseEnter={handleOpen}
                onMouseLeave={handleClose}
                sx={{ height, width: '100%', minWidth: 0 }}
            >
                <Box sx={{ pointerEvents: 'none' }}>
                    <Chart
                        type="bar"
                        series={chartOptions.series}
                        options={chartOptions}
                        height={height}
                        width="100%"
                    />
                </Box>
            </Box>
        </CustomTooltip>
    );
}


